import clsx from 'clsx'
import { useState } from 'react'

import { useUserApi } from '@/api/useUserApi'
import {
  PRINT_LABELS_MODAL_ID,
  PrintLabelsModal,
} from '@/app/order-confirmation/PrintLabelsModal'
import useSessionStore from '@/stores/useSessionStore'
import { isAdmin } from '@/utils/adminProRedirect'

import { MOBILE_VERIFY_MODAL_ID } from 'lib/components/MobileVerification/constants'
import { getShipmentDetails } from 'lib/utils/orderUtils'
import { transformLegToRouteSnapshotData } from 'lib/utils/routeSnapshotUtils'
import { useCallback, useMemo } from 'react'
import Button from '../Button/Button'
import Header from '../Header/Header'
import Link from '../Link/Link'
import { useModal } from '../Modal/Modal'
import ShareItineraryModal from '../NextStepsOverview/partials/ShareItineraryModal'
import { RouteSnapshotShipmentProps } from '../RouteSnapshot/partials/RouteSnapshotShipment'
import RouteSnapshot, {
  RouteSnapshotButtonHandlers,
} from '../RouteSnapshot/RouteSnapshot'
import { ShipmentDetailsModalProps } from '../ShipmentDetailsModal/ShipmentDetailsModal'
import orderTrackingButtons from './orderTrackingButtons'

import { LOGIN_MODAL_ID } from '../LoginModal/LoginModal'
import OrderActionButtons from './OrderActionButtons'
import OrderPaymentDetails from './OrderPaymentDetails'

export interface OrderTrackingButtonHandlers {
  login?: () => void
  printAllLabels?: (orderId: string) => void
  printShipment?: (trackingId: string) => void
  shareItinerary?: (orderId: string) => void
  getShipmentDetails?: (shipmentItem: OrderShipment) => void
}

export interface OrderTrackingProps {
  /**
   * The order data
   */
  order: Order
  /**
   * Optional click handler for the button
   */
  onButtonClick?: (order: Order) => void
  /**
   * Whether to expand the shipment details
   */
  expandShipments?: boolean
  isLoggedIn?: boolean
  options?: Record<string, any>
  /**
   * Optional: will show details if a shipment is active
   */
  activeShipmentId?: string
  /**
   * Optional phone number for verification
   */
  phoneNumber?: string
  /**
   * Allow unauthenticated guests to see and use print label buttons (Sticks/Skis/Ship&Play only)
   */
  allowGuestPrintLabel?: boolean
}

const defaultOptions = {
  border: 'lg:border lg:border-neutral-400',
  padding: 'p-3 lg:p-8 lg:rounded-8',
  showPaymentDetails: true,
  showHeaderButtons: true,
  showPromoCard: true,
  shipmentOptions: {
    showShipper: true,
    showLegDetails: true,
    showShipmentDetails: true,
  },
}

export default function OrderTracking({
  order,
  onButtonClick,
  expandShipments = false,
  isLoggedIn = false,
  options = {},
  activeShipmentId,
  phoneNumber,
  allowGuestPrintLabel,
}: OrderTrackingProps) {
  const [legType, setLegType] = useState<'outbound' | 'return' | 'all'>('all')
  const authToken = useSessionStore((state: any) => state.authToken)
  const currentUserId = useSessionStore((state: any) => state.userId)
  const userRoles = useSessionStore((state: any) => state.userRoles)
  const mobileVerified = useSessionStore((state: any) => state.mobileVerified)
  const isGuestUser = Boolean(userRoles?.includes('guest_user'))
  // Email CTAs land here with the order's user id under one of three param
  // names depending on which link was clicked: `guest_id` (Braze label
  // emails), `id` (force-registration links), or `_id` (Rails' own
  // labels#print_all / orders#show bounce). Read all three — reading only
  // `guest_id` left the OTP with nobody to attribute it to, and Rails then
  // rejects every correct code with "refresh the page and try again".
  const { guestIdParam, identityParam } = useMemo(() => {
    if (typeof window === 'undefined') {
      return { guestIdParam: null, identityParam: null }
    }
    const searchParams = new URLSearchParams(window.location.search)
    const guest = searchParams.get('guest_id')
    return {
      guestIdParam: guest,
      identityParam: guest ?? searchParams.get('id') ?? searchParams.get('_id'),
    }
  }, [])
  // Button visibility stays keyed on `guest_id` alone — broadening it would
  // expose actions to arrivals that previously didn't get them. Only the
  // verification gate below consumes the wider `identityParam`.
  const hasGuestIdParam = Boolean(guestIdParam)
  // Deliberately NOT falling back to `order.user.id` here. The backend trusts
  // a supplied user id outright when there's no session, and the order payload
  // is fetchable with just an order id — so deriving the OTP target from the
  // order would let anyone holding an order id verify that order's user with
  // their own phone (which `update_sms_properties` then writes to the record).
  // Identity has to come from the emailed link, or be proven by the email step
  // below. Arrivals with neither still get attributed server-side via
  // `orderId` + the phone-ownership check in MobileVerificationUpdate.
  const verifyTargetUserId = identityParam
  const isUnauthenticatedGuest = !authToken && !verifyTargetUserId
  const userQuery = useUserApi(!!authToken)
  // Same rule as the order-confirmation page: anyone unverified must
  // SMS-verify before printing labels — guest or registered, no exceptions.
  // Verified when ANY source says so: the session store (set synchronously by
  // MobileVerifyModal on a successful verify), the backend user record, or the
  // order's own user. That last one matters: `userQuery` is disabled without a
  // token, so a token-less arrival by an ALREADY-verified user used to read as
  // unverified and got forced into an OTP it could never complete.
  const verified =
    mobileVerified ||
    userQuery.data?.mobileVerified === true ||
    order.user?.mobile_verified === true
  const shouldVerifyLabels = !verified
  const customOptions = useMemo(() => {
    return { ...defaultOptions, ...options }
  }, [options])

  const { openDialog, hideDialog } = useModal()

  const handleClosePrintLabels = useCallback(() => {
    console.log('closed print labels modal')
    setLegType('all')
  }, [])

  const handlePrintShipment = useCallback(
    (trackingId: string) => {
      const outboundShipment = order.legs?.[0]?.travelers?.some((traveler) =>
        traveler.shipments?.some((s) => s.tracking_id === trackingId),
      )
      const nextLegType = outboundShipment ? 'outbound' : 'return'
      setLegType(nextLegType)
      hideDialog(PRINT_LABELS_MODAL_ID)

      if (!shouldVerifyLabels) {
        openDialog(PRINT_LABELS_MODAL_ID, {
          orderId: order.id,
          trackingIds: [trackingId],
        })
        return
      }

      openDialog(MOBILE_VERIFY_MODAL_ID, {
        phoneNumber: isUnauthenticatedGuest ? undefined : phoneNumber,
        requiresEmailStep: isUnauthenticatedGuest,
        guestUserId: verifyTargetUserId ?? undefined,
        afterSuccessModalId: PRINT_LABELS_MODAL_ID,
        afterSuccessModalArgs: {
          orderId: order.id,
          trackingIds: [trackingId],
        },
      })
    },
    [
      hideDialog,
      isUnauthenticatedGuest,
      openDialog,
      order.id,
      order.legs,
      phoneNumber,
      shouldVerifyLabels,
      verifyTargetUserId,
    ],
  )

  const handlePrintAllLabels = useCallback(() => {
    setLegType('all')

    if (shouldVerifyLabels) {
      openDialog(MOBILE_VERIFY_MODAL_ID, {
        phoneNumber: isUnauthenticatedGuest ? undefined : phoneNumber,
        requiresEmailStep: isUnauthenticatedGuest,
        guestUserId: verifyTargetUserId ?? undefined,
        afterSuccessModalId: PRINT_LABELS_MODAL_ID,
        afterSuccessModalArgs: {
          orderId: order.id,
        },
      })
      return
    }

    openDialog(PRINT_LABELS_MODAL_ID, { orderId: order.id })
  }, [
    isUnauthenticatedGuest,
    openDialog,
    order.id,
    phoneNumber,
    shouldVerifyLabels,
    verifyTargetUserId,
  ])

  const shipper: Shipper = useMemo(() => {
    const firstShipment = order.legs?.[0].travelers?.[0].shipments?.[0]
    return (firstShipment?.carrier_service_level.carrier.code ||
      'fedex') as Shipper
  }, [order.legs])

  const transformLegToRouteSnapshot = useCallback(
    (leg: OrderLeg) => {
      // Get core transformation using utility
      const coreData = transformLegToRouteSnapshotData(leg, {
        shipper,
        isUserAuthenticated: isLoggedIn,
      })

      return {
        ...coreData,
        buttonHandlers: {
          printShipment: handlePrintShipment,
        } as RouteSnapshotButtonHandlers,
        isUserAuthenticated: isLoggedIn,
        orderUserId: order.user?.id || order.user_id,
        currentUserId,
        userRoles,
        allowGuestPrintLabel,
      }
    },
    [
      allowGuestPrintLabel,
      handlePrintShipment,
      isLoggedIn,
      shipper,
      order.user?.id,
      order.user_id,
      currentUserId,
      userRoles,
    ],
  )

  const transformedLegs = useMemo(
    () => order.legs.map(transformLegToRouteSnapshot),
    [order.legs, transformLegToRouteSnapshot],
  )

  const handleOnClickShipmentDetails = useCallback(() => {
    if (!isLoggedIn) {
      openDialog(LOGIN_MODAL_ID)
    }
  }, [isLoggedIn, openDialog])

  const handleGetShipmentDetails: RouteSnapshotShipmentProps['getShipmentDetails'] =
    useCallback(
      async (
        tracking_id: string,
      ): Promise<Omit<ShipmentDetailsModalProps, 'id'>> => {
        return getShipmentDetails(authToken, tracking_id)
      },
      [authToken],
    )

  const canViewPaymentDetails = useMemo(() => {
    const orderUserId = order.user?.id || order.user_id

    if (!isLoggedIn) {
      return false
    }

    if (!currentUserId) {
      return false
    }

    if (!orderUserId) {
      return false
    }

    const isOrderUser = currentUserId === orderUserId
    const userIsAdmin = isAdmin(userRoles)

    return isOrderUser || userIsAdmin
  }, [isLoggedIn, currentUserId, order.user?.id, order.user_id, userRoles])

  const filteredButtons = useMemo(() => {
    const orderUserId = order.user?.id || order.user_id

    return orderTrackingButtons
      .filter((button) => {
        if (button.id === 'edit-order') {
          // Brand-gated: /orders/:id/edit 404s when user-edit-order is disabled.
          if (!order.brand?.user_edit_order_enabled) {
            return false
          }

          if (!orderUserId) {
            return false
          }

          if (!currentUserId) {
            return false
          }

          const isOrderUser = currentUserId === orderUserId
          const userIsAdmin = isAdmin(userRoles)

          return isOrderUser || userIsAdmin
        }

        if (button.id === 'print-all-labels') {
          if (hasGuestIdParam) {
            return true
          }

          if (allowGuestPrintLabel) {
            return true
          }

          if (!orderUserId) {
            return false
          }

          if (!currentUserId) {
            return false
          }

          const isOrderUser = currentUserId === orderUserId
          const userIsAdmin = isAdmin(userRoles)

          return isOrderUser || userIsAdmin
        }

        if (
          hasGuestIdParam &&
          (button.id === 'share-itinerary' || button.id === 'print-receipt')
        ) {
          return true
        }

        if (button.loggedInOnly) {
          return isLoggedIn
        }
        return true
      })
      .map((button) => {
        if (button.id === 'print-all-labels') {
          return {
            ...button,
            onClick: handlePrintAllLabels,
          }
        }

        return button
      })
  }, [
    allowGuestPrintLabel,
    order.user?.id,
    order.user_id,
    order.brand?.user_edit_order_enabled,
    currentUserId,
    userRoles,
    isLoggedIn,
    handlePrintAllLabels,
    hasGuestIdParam,
  ])

  return (
    <div
      className={clsx(
        customOptions?.border || 'lg:border lg:border-neutral-400',
        customOptions?.padding || 'lg:p-8 lg:rounded-8',
      )}
    >
      <header className="flex md-max:flex-col gap-y-4 lg:justify-between">
        <div>
          <Header as="h2" className="type-body-1">
            Order ID:{' '}
            <Link
              href={`/order-tracking?order_id=${order.order_id}`}
              className="text-theme-accessibility hover:underline"
              inheritFont
            >
              {order.order_id}
            </Link>
          </Header>
          {customOptions?.showPaymentDetails && (
            <OrderPaymentDetails
              order={order}
              className="custom-payment-details flex flex-row items-center"
              currency="USD"
              locale="en-US"
              canViewPaymentDetails={canViewPaymentDetails}
            />
          )}
        </div>
        {expandShipments && customOptions?.showHeaderButtons && (
          <OrderActionButtons
            orderId={order.order_id}
            isLoggedIn={isLoggedIn}
            buttons={filteredButtons}
            isGuestUser={isGuestUser}
            hasGuestIdParam={hasGuestIdParam}
            openModal={openDialog}
          />
        )}
        {!expandShipments && (
          <Button
            type="button"
            className="md-max:w-full"
            onClick={() => onButtonClick?.(order)}
          >
            Track order
          </Button>
        )}
      </header>
      {/* -mx-3 cancels the wrapper's mobile p-3 exactly so the card runs
          edge-to-edge. It was -mx-6, overshooting by 12px a side and pushing
          the page 12px wider than the viewport (horizontal scroll on mobile). */}
      <div className="mt-6 md-max:-mx-3 flex flex-col gap-y-6">
        {transformedLegs.map((legData, index) => (
          <RouteSnapshot
            key={index}
            {...legData}
            getShipmentDetails={handleGetShipmentDetails}
            onClickShipmentDetails={handleOnClickShipmentDetails}
            isUserAuthenticated={isLoggedIn}
            activeShipmentId={activeShipmentId}
            options={customOptions.shipmentOptions || {}}
          />
        ))}
      </div>
      <ShareItineraryModal orderId={order.id} />
      <PrintLabelsModal
        order={order}
        legType={legType}
        afterHide={handleClosePrintLabels}
        isAdmin={isAdmin(userRoles)}
      />
    </div>
  )
}
